import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import '../customization-page.css';
import { CustomizationObjectType, ModelType } from '../../../store/global-types/customization-types';
import { MVisionAppClient } from '../../../store/configurationTarget/mvision-client-list';
import { StoreState } from '../../../store/store';
import CustomizationToolbar from '../CustomizationToolbar';
import { adaptSelectors, allCustomizationChangesReverted, customizationBaseDuplicated, customizationBaseRemoved, customizationRenamed, modelCustomizationSaved, singleCustomizationReset } from '../../../store/adapt/adaptSlice';
import { hasBlockingFormValidationErrors } from '../../../store/global-types/store-errors';

interface AdaptCustomizationToolbarProps {
    customizationOutputId?: string,
    currentView: CustomizationObjectType,
}

const AdaptCustomizationToolbar = (props: AdaptCustomizationToolbarProps) => {
    const { customizationOutputId, currentView } = props;

    const dispatch = useDispatch();

    const modelCustomizationSaveError = useSelector((state: StoreState) => state.adapt.modelCustomizationSaveError);
    const modelCustomizationDataError = useSelector((state: StoreState) => state.adapt.modelCustomizationDataError);
    const concurrentSaveError = useSelector((state: StoreState) => state.adapt.concurrentSaveError);
    const isSavingInProgress = useSelector((state: StoreState) => state.adapt.isModelCustomizationSavingInProgress);

    // needed for duplicated model name validation
    const allCustomizationNamesAndModelIds = useSelector(adaptSelectors.selectAllCustomizationNamesAndParentModelIds);

    const isAnyCustomizationModelModified = useSelector(adaptSelectors.selectIsAnyCustomizationModelModified);
    const hasFormValidationErrors = hasBlockingFormValidationErrors(useSelector(adaptSelectors.selectFormValidationErrors));

    const customizationOutput = useSelector((state: StoreState) => customizationOutputId ? adaptSelectors.selectOutputById(state, customizationOutputId) : undefined);
    const customizationBase = useSelector((state: StoreState) => customizationOutput ? adaptSelectors.selectCustomizationBaseById(state, customizationOutput.modelCustomizationBaseId) : undefined);
    const segmentationModel = useSelector((state: StoreState) => customizationBase ? adaptSelectors.selectModelById(state, customizationBase.modelId) : undefined);
    const isCustomizationOutputResetInProgress = useSelector((state: StoreState) => state.adapt.isOutputResetInProgress);

    const handleUndo = useCallback(() => {
        dispatch(allCustomizationChangesReverted());
    }, [dispatch]);

    const handleSave = useCallback((currentTarget: MVisionAppClient, force?: boolean) => {
        dispatch(modelCustomizationSaved({ configurationTarget: currentTarget, force }));
    }, [dispatch]);

    const handleAddNewCustomization = useCallback((customizationBaseId: string, newCustomizationName: string) => {
        dispatch(customizationBaseDuplicated({ customizationBaseId, newCustomizationName }));
    }, [dispatch]);

    const handleCustomizationDelete = useCallback((customizationBaseId: string) => {
        dispatch(customizationBaseRemoved(customizationBaseId));
    }, [dispatch]);

    const handleCustomizationReset = useCallback((customizationBaseId: string) => {
        dispatch(singleCustomizationReset(customizationBaseId));
    }, [dispatch]);

    const handleRenameCustomization = useCallback((customizationBaseId: string, newCustomizationName: string) => {
        dispatch(customizationRenamed({ customizationBaseId, name: newCustomizationName }));
    }, [dispatch]);

    return (
        <CustomizationToolbar
            currentView={currentView}
            customizationOutputId={customizationOutputId}
            modelType={ModelType.Adapt}

            allCustomizationNamesAndModelIds={allCustomizationNamesAndModelIds}
            isAnyCustomizationModelModified={isAnyCustomizationModelModified}
            customizationOutput={customizationOutput}
            customizationBase={customizationBase}
            model={segmentationModel}

            modelCustomizationSaveError={modelCustomizationSaveError}
            modelCustomizationDataError={modelCustomizationDataError}
            concurrentSaveError={concurrentSaveError}
            isSavingInProgress={isSavingInProgress}
            // intentionally use "output reset" state here in place of "customization reset state" -- under the hood
            // it calls the exact same API
            isCustomizationResetInProgress={isCustomizationOutputResetInProgress}
            isCustomizationOutputResetInProgress={false}

            onUndo={handleUndo}
            onSave={handleSave}
            onAddNewCustomization={handleAddNewCustomization}
            onDeleteCustomization={handleCustomizationDelete}
            onResetCustomization={handleCustomizationReset}
            onRenameCustomization={handleRenameCustomization}
            hasFormValidationErrors={hasFormValidationErrors}
        />
    );
}

export default AdaptCustomizationToolbar;
